// AgreementsRegisterViewController.tsx
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import AgreementsRegisterView from './AgreementsRegisterView';
import { Creators as agreementInfoActions } from '@/domain/useCases/scholarshipsDiscounts/getAgreementInfo/actions';
import { Creators as registerAgreementActions } from '@/domain/useCases/scholarshipsDiscounts/registerAgreement/actions';
import routes from '@/presentation/components/Routes/routingMap';

const PROCEDURE_CODE = 1039;

interface LocationState {
  selectedBeneficiary?: string;
  beneficiaryName?: string;
  documents?: string[];
  requirementsAuto?: { text: string; valid: boolean }[];
  dniColaborador?: string;
  dniDependiente?: string;
}

const AgreementsRegisterViewController: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const dispatch = useDispatch();
  const { type } = useParams<{ type: string }>();
  const { isLoading: isLoadingInfo, data: agreementData } = useSelector(
    (state: any) => state.getAgreementInfo
  );
  const {
    data: registerData,
    error: registerError,
    isLoading: isLoadingRegister
  } = useSelector((state: any) => state.registerAgreement);

  const [filesToSend, setFilesToSend] = useState<
    { originalName: string; base64: string }[]
  >([]);
  const [filesModified, setFilesModified] = useState(false);
  const [showModalModified, setShowModalModified] = useState(false);
  const [showModalError, setShowModalError] = useState(false);
  const [validateDocuments, setValidateDocuments] = useState(false);

  const selectedBeneficiary = useMemo(() => {
    if (location.state?.selectedBeneficiary)
      return location.state.selectedBeneficiary;
    if (
      agreementData?.beneficiaries &&
      agreementData.beneficiaries.length > 0
    ) {
      return agreementData.beneficiaries[0].id;
    }
    return '';
  }, [agreementData, location.state]);

  const beneficiaryName = location.state?.beneficiaryName ?? '';
  const documentsFromState = location.state?.documents ?? [];
  const requirementsAuto = location.state?.requirementsAuto ?? [];

  useEffect(() => {
    if (!agreementData) {
      dispatch(agreementInfoActions.getAgreementInfo(PROCEDURE_CODE));
    }
  }, [dispatch, agreementData]);

  const requestId = registerData?.id;

  useEffect(() => {
    if (requestId > 0) {
      history.push(routes.student.procedures.home);
    }
  }, [dispatch, history, requestId, type]);

  const handleFilesChange = useCallback((files: any[]) => {
    setFilesToSend(files);
    setFilesModified(true);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!agreementData) return;
    const { dniColaborador, dniDependiente } = location.state ?? {};
    const payload = {
      procedureCode: PROCEDURE_CODE,
      beneficiaryTypeId: selectedBeneficiary,
      beneficiaryTypeDescription: beneficiaryName,
      dniColaborador,
      dniDependiente,
      conventionDiscount: agreementData.discount ?? 0,
      attachedFiles: filesToSend.map((file) => ({
        fileName: file.originalName,
        fileBase64: file.base64
      }))
    };
    dispatch(registerAgreementActions.registerAgreement(payload));
  }, [
    agreementData,
    beneficiaryName,
    dispatch,
    filesToSend,
    location.state,
    selectedBeneficiary
  ]);

  const handleBack = useCallback(() => {
    if (filesModified) {
      setShowModalModified(true);
      return;
    }
    history.goBack();
  }, [filesModified, history]);

  const handleCancelModalModified = useCallback(() => {
    setShowModalModified(false);
  }, []);

  const handleQuitModalModified = useCallback(() => {
    history.goBack();
  }, [history]);

  useEffect(() => {
    if (registerError || registerData?.state === 'ERROR') {
      setShowModalError(true);
    }
  }, [registerError, registerData?.state]);

  const handleClickModalError = useCallback(() => {
    history.push(routes.student.procedures.home);
  }, [history]);

  const documentsList = useMemo(
    () => (documentsFromState.length > 0 ? documentsFromState : []),
    [documentsFromState]
  );

  const procedureSelectedInfo = useMemo(() => {
    if (!agreementData) {
      return {
        steps: [] as string[],
        name: '',
        amount: '',
        duration: '',
        endDate: '',
        isLoading: true
      };
    }
    const customName =
      agreementData.title?.toLowerCase() === 'intercorp'
        ? 'Convenio de Grupo Intercorp'
        : agreementData.title ?? '';
    return {
      steps: agreementData.steps ?? [],
      name: customName,
      amount: agreementData.cost ?? '',
      duration: agreementData.attentionDuration ?? '',
      endDate: agreementData.endDate ?? '',
      isLoading: isLoadingInfo
    };
  }, [agreementData, isLoadingInfo]);

  return (
    <AgreementsRegisterView
      beneficiaryName={beneficiaryName}
      documentsList={documentsList}
      requirementsAuto={requirementsAuto}
      onSubmit={handleSubmit}
      handleBack={handleBack}
      isLoading={isLoadingInfo}
      isProcessing={isLoadingRegister}
      procedureSelectedInfo={procedureSelectedInfo}
      base64={filesToSend}
      maxFiles={documentsList.length > 0 ? documentsList.length : 1}
      maxSizeMB={4}
      emitFiles={handleFilesChange}
      showModalModified={showModalModified}
      handleQuitModalModified={handleQuitModalModified}
      handleCancelModalModified={handleCancelModalModified}
      redirectToTicket={() =>
        history.push(
          `${routes.student.procedures.categories.tickets.register}?v=TA0047`
        )
      }
      showModalError={showModalError}
      handleCloseModalError={handleClickModalError}
      handleClickModalError={handleClickModalError}
      validateDocuments={validateDocuments}
      setValidateDocuments={setValidateDocuments}
    />
  );
};

export default AgreementsRegisterViewController;
